import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./PublicPageShared.css";
import "./PricingPageV2.css";

import {
  Button,
  Spinner,
  toast,
  cn,
  CheckIcon,
} from "../../../styles/facemeet-ui";

import { Brand, ThemeSlot, Ambient } from "./PublicHelpers";
import auth from "../authV2";
import { logger } from "../../../log/logger";

const API_URL = process.env.REACT_APP_SERVER_URL;

/* Human price from paise. Free → "Free". */
function formatPrice(amount, currency) {
  if (!amount || amount <= 0) return "Free";
  const major = amount / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: currency || "INR", maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `₹${major}`;
  }
}

/* Turn a features object into display rows. Order is intentional. */
const FEATURE_ROWS = [
  ["maxParticipants", (v) => `Up to ${v} participants`],
  ["meetingDuration", (v) => `${v}-minute meetings`],
  ["recording", (v) => (v ? "Cloud recording" : null)],
  ["screenShare", (v) => (v ? "Screen sharing" : null)],
  ["waitingRoom", (v) => (v ? "Waiting room" : null)],
  ["branding", (v) => (v ? "Custom branding" : null)],
  ["prioritySupport", (v) => (v ? "Priority support" : null)],
];

function featureLines(features = {}) {
  const lines = [];
  for (const [key, fn] of FEATURE_ROWS) {
    const label = fn(features[key]);
    if (label) lines.push(label);
  }
  return lines;
}

export function PricingPageV2() {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [session, setSession] = useState(() => auth.getSession());
  const [selected, setSelected] = useState(null);   // planCode
  const [busyCode, setBusyCode] = useState(null);    // plan being purchased

  const currentPlan = session?.currentPlan || "free";

  /* Load plans + refresh session (for currentPlan). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Refresh session so currentPlan is up to date (best-effort).
      try {
        const fresh = await auth.validate();
        if (!cancelled && fresh) setSession(fresh);
      } catch { /* keep cached */ }

      try {
        const res = await fetch(`${API_URL}/v2/plans`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const items = data.items || [];
        setPlans(items);
        // Free pre-selected by default (or the default plan).
        const def = items.find((p) => p.isDefault) || items.find((p) => p.interval === "free");
        setSelected(def ? def.planCode : (items[0] && items[0].planCode));
      } catch (e) {
        if (!cancelled) setError("Couldn't load plans. Please refresh.");
        logger.error("pricing_plans_load_failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isFree = useCallback(
    (plan) => plan.interval === "free" || !plan.priceAmount,
    [],
  );

  /* Buy flow: create-order → Razorpay checkout → verify-payment. */
  const handleChoose = async (plan) => {
    setSelected(plan.planCode);

    if (isFree(plan)) {
      // Selecting Free needs no payment. If they're on a paid plan it
      // will lapse naturally at period end; here we just acknowledge.
      toast.success("You're on the Free plan.");
      return;
    }

    if (!session?.token || !session?.fdaId) {
      toast.warning("Please sign in first to upgrade.");
      navigate("/v2/login");
      return;
    }

    if (plan.planCode === currentPlan) {
      toast.info("You're already on this plan.");
      return;
    }

    setBusyCode(plan.planCode);
    try {

      // 1. Create a (mock) order on our backend.
      const orderRes = await fetch(`${API_URL}/v2/billing/create-order`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            planCode: plan.planCode,
            fdaId: session.fdaId,
          }),
        });

        const order = await orderRes.json();

        if (!orderRes.ok) {
          toast.error(order.detail || "Couldn't start checkout.");
          return;
        } 
          try {
          const vRes = await fetch(
              `${API_URL}/v2/billing/verify-payment`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  orderId: order.orderId,
                  fdaId: session.fdaId,
                }),
              }
            );

            const v = await vRes.json();

            if (!vRes.ok) {
              toast.error(v.detail || "Verification failed");
              return;
            }
            toast.success(`${order.displayName} unlocked!`);
            // Refresh session so currentPlan updates immediately.
            // We try the soft path first (auth.validate → /auth/me → merge),
            // but the user has occasionally reported the UI still showing
            // the old plan after Razorpay returns. Most likely causes are
            // (a) /auth/me caching a stale response, (b) the localStorage
            // write losing a race with another tab. Hard-reload after a
            // short delay is the bulletproof fallback — guarantees every
            // bit of state on the next render comes from the server.
            try {
              const fresh = await auth.validate();
              if (fresh && fresh.currentPlan === plan.planCode) {
                setSession(fresh);
                // Optimistic path worked; no reload needed.
                return;
              }
            } catch (_) { /* fall through to reload */ }
            setTimeout(() => { window.location.reload(); }, 600);
          } catch {
            toast.error("Payment verification failed. If charged, contact support.");
          }
    } catch (e) {
      logger.error("pricing_checkout_failed", e);
      toast.error("Something went wrong starting checkout.");
    } finally {
      setBusyCode(null);
    }
  };

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [plans],
  );

  return (
    <div className="fm-page">
      <Ambient />
      <ThemeSlot />

      <div className="fm-page__inner" style={{ maxWidth: 1040 }}>
        <Brand tagline="Choose the plan that fits your meetings." />

        {loading && (
          <div className="fm-blockstate"><Spinner size="lg" /><p>Loading plans…</p></div>
        )}

        {!loading && error && (
          <div className="fm-blockstate">
            <div className="fm-blockstate__icon" aria-hidden="true">⚠️</div>
            <p>{error}</p>
            <Button variant="secondary" onClick={() => window.location.reload()}>Retry</Button>
          </div>
        )}

        {!loading && !error && (
          <div className="fm-pricing-grid">
            {sortedPlans.map((plan) => {
              const active = selected === plan.planCode;
              const isCurrent = currentPlan === plan.planCode;
              const free = isFree(plan);
              // If the user is on a paid plan, the Free card is shown
              // as a passive default (not actionable). They keep their
              // paid benefits for the rest of the billing cycle; Free
              // is what they fall back to after expiry (handled by the
              // downgrade cron). "Temporarily disabled" in product
              // terms = visually present but uninteractive.
              const userIsOnPaid = currentPlan && currentPlan !== "free";
              const freeDormantForPaidUser = free && userIsOnPaid;
              return (
                <section
                  key={plan.planCode}
                  className={cn(
                    "fm-plan-card",
                    active && "fm-plan-card--selected",
                    plan.branding && "fm-plan-card--featured",
                    freeDormantForPaidUser && "fm-plan-card--dormant",
                  )}
                  onClick={() => !freeDormantForPaidUser && setSelected(plan.planCode)}
                >
                  {isCurrent && <span className="fm-plan-card__badge">Current plan</span>}
                  {freeDormantForPaidUser && (
                    <span className="fm-plan-card__badge fm-plan-card__badge--muted">
                      Default fallback
                    </span>
                  )}

                  <div className="fm-plan-card__head">
                    <h3 className="fm-plan-card__name">{plan.displayName}</h3>
                    <div className="fm-plan-card__price">
                      {formatPrice(plan.priceAmount, plan.currency)}
                      {!free && <span className="fm-plan-card__interval">/mo (PAY VIA FDA)</span>}
                    </div>
                    {plan.description && (
                      <p className="fm-plan-card__desc">{plan.description}</p>
                    )}
                  </div>

                  <ul className="fm-plan-card__features">
                    {featureLines(plan.features).map((line) => (
                      <li key={line}>
                        <CheckIcon size={15} /> <span>{line}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={free ? "secondary" : (active ? "primary" : "secondary")}
                    fullWidth
                    disabled={isCurrent || freeDormantForPaidUser}
                    loading={busyCode === plan.planCode}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (freeDormantForPaidUser) return;   // belt + braces
                      handleChoose(plan);
                    }}
                  >
                    {isCurrent
                      ? "Your plan"
                      : freeDormantForPaidUser
                        ? "Resumes after your plan ends"
                        : free
                          ? "Get started"
                          : "Choose " + plan.displayName}
                  </Button>
                </section>
              );
            })}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "var(--space-6, 24px)" }}>
          <Button variant="ghost" size="sm" onClick={() => navigate("/v2")}>← Back to home</Button>
        </div>
      </div>
    </div>
  );
}

export default PricingPageV2;